import { useMemo, useState } from 'react';
import { Box, Button, InputAdornment, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { downloadCSV, makeCSV } from '../../components/CustomTableWithPagination/csvExport';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import dayjs from 'dayjs';
import { readDefaultDepartment } from './viewerDepartment';

// Reusable client-side table for the Warning Dashboard's metric detail pages: search, an optional department
// filter, and CSV export - operating on rows already fully loaded client-side (none of these dashboards
// paginate). Built instead of switching these pages to GlobalTable: GlobalTable's search/export both require
// server-side wiring (search re-fetches via a thunk, export needs a Redux thunk or a dedicated backend
// CSV-streaming endpoint) that these small, already-fully-loaded datasets don't need - confirmed with the
// user 2026-10-01 that would be more work, not less, for pages this size.
//
// Department filter only renders when the data actually spans more than one department - an OGD/Project-dept
// viewer (performanceMetricsScope-restricted) only ever gets rows from their own department, so the filter
// would be a single, useless option; a global viewer (Finance, Top Management, etc.) sees every department
// mixed together and the filter becomes genuinely useful.
// The pages show a deactivated person as "Name (Inactive)"; the export used to write the bare name, so the sheet
// lost the marker (BUG: "Export keeps row status"). Every page's name column is `entityName`, so this one rule
// keeps all of them in step with the screen. A column that sets its own `csvValue` is left alone.
export const INACTIVE_SUFFIX = ' (Inactive)';
const inactiveAwareName = ({ row }) => `${row.entityName ?? ''}${row.isActive === false ? INACTIVE_SUFFIX : ''}`;

const WarningMetricTable = ({
  rows,
  columns, // [{ field, headerName, align, render?: (row) => node, csvValue?: (row) => value }]
  searchFields = ['entityName'],
  deptField = 'departmentId',
  departmentName, // (id) => string, from the page's own DEPARTMENTS/PROJECTS list
  deptLabel = 'Department', // 'Project' for the Project-wise model (ACL/CCR/FDR/SAR/ATP), which groups by projectId instead
  emptyMessage = 'No data for this period.',
  exportFileName = 'Export',
  rowKey,
  // optional: (filteredRows, allRows) => [{ key, cells: { [field]: node }, emphasis? }] - bold summary rows under the data,
  // computed from what is currently shown. Omit it and the table is exactly as before.
  footer,
  // optional: a second dropdown over a row field (the Lead Category on LCR). Shown only when the rows really span more
  // than one value; `categoryOrder` lists the values in the order they should appear (anything else follows, A-Z).
  categoryField,
  categoryLabel = 'Lead category',
  categoryAllLabel = 'All categories',
  categoryOrder = [],
}) => {
  const [search, setSearch] = useState('');
  // The Department filter. OGD people and the project departments' people start on THEIR department (viewerDepartment.js); everyone
  // else on all. `dept` is null until the person picks something ('' = they picked "All"), so a choice is never overridden - not by
  // new rows, not by changing the month. The default is only applied when their department is among the options.
  const defaultDept = useMemo(() => readDefaultDepartment(), []);
  const [dept, setDept] = useState(null);
  const [category, setCategory] = useState('');

  const deptOptions = useMemo(
    () => [...new Set(rows.map((r) => r[deptField]).filter((v) => v !== undefined && v !== null))],
    [rows, deptField],
  );

  // each category present in the data, with how many rows it has, in the page's preferred order
  const categoryOptions = useMemo(() => {
    if (!categoryField) return [];
    const counts = new Map();
    rows.forEach((r) => {
      const value = r[categoryField];
      if (value !== undefined && value !== null && value !== '') counts.set(value, (counts.get(value) || 0) + 1);
    });
    const rank = (value) => (categoryOrder.indexOf(value) === -1 ? categoryOrder.length : categoryOrder.indexOf(value));
    return [...counts.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || String(a[0]).localeCompare(String(b[0])));
  }, [rows, categoryField, categoryOrder]);

  const startDept = defaultDept !== null && deptOptions.length > 1 ? deptOptions.find((o) => String(o) === String(defaultDept)) ?? '' : '';
  const activeDept = dept === null ? startDept : dept;

  const filteredRows = useMemo(() => {
    let out = rows;
    if (activeDept) out = out.filter((r) => String(r[deptField]) === String(activeDept));
    if (category && categoryField) out = out.filter((r) => String(r[categoryField]) === String(category));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((r) => searchFields.some((f) => String(r[f] ?? '').toLowerCase().includes(q)));
    }
    return out;
  }, [rows, activeDept, category, categoryField, search, searchFields, deptField]);

  const handleExport = () => {
    const headers = columns.map((c) => ({
      field: c.field,
      headerName: c.headerName,
      valueGetter: c.csvValue ? ({ row }) => c.csvValue(row) : c.field === 'entityName' ? inactiveAwareName : undefined,
    }));
    const csv = makeCSV({ headers, rows: filteredRows });
    downloadCSV(csv, `${exportFileName}_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`);
  };

  return (
    <Box>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
        />
        {deptOptions.length > 1 && (
          <TextField select size="small" label={deptLabel} value={activeDept} onChange={(e) => setDept(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All {deptLabel.toLowerCase()}s</MenuItem>
            {deptOptions.map((id) => (
              <MenuItem key={id} value={id}>
                {departmentName ? departmentName(id) : id}
              </MenuItem>
            ))}
          </TextField>
        )}
        {categoryOptions.length > 1 && (
          <TextField select size="small" label={categoryLabel} value={category} onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="">{categoryAllLabel}</MenuItem>
            {categoryOptions.map(([value, count]) => (
              <MenuItem key={value} value={value}>
                {value} ({count})
              </MenuItem>
            ))}
          </TextField>
        )}
        <Button variant="outlined" onClick={handleExport} disabled={filteredRows.length === 0}>
          Export CSV
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredRows.filter((r) => !r.excludeFromCount).length} of {rows.filter((r) => !r.excludeFromCount).length} shown
        </Typography>
      </Box>

      {filteredRows.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#fff', border: '1px dashed #E6E2D9', borderRadius: '14px' }}>
          <Typography color="text.secondary">{rows.length === 0 ? emptyMessage : 'Nothing matches your search/filter.'}</Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c.field} align={c.align || 'left'}>
                    {c.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={rowKey ? rowKey(row) : row.entityId} hover sx={row.isWarning ? { boxShadow: 'inset 3px 0 0 #D92D20' } : undefined}>
                  {columns.map((c) => (
                    <TableCell key={c.field} align={c.align || 'left'}>
                      {c.render ? c.render(row) : (row[c.field] ?? '-')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {footer &&
                footer(filteredRows, rows).map((f) => (
                  <TableRow
                    key={f.key}
                    sx={{ '& td': { fontWeight: 700, bgcolor: f.emphasis ? '#EEF2FF' : '#FAF8F3', borderTop: f.first ? '2px solid #E6E2D9' : undefined } }}
                  >
                    {columns.map((c) => (
                      <TableCell key={c.field} align={c.align || 'left'}>
                        {f.cells[c.field] ?? ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default WarningMetricTable;
